import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaStore, FaMapMarkerAlt, FaPhone, FaFileAlt,
  FaStar, FaShieldAlt, FaArrowRight, FaExclamationTriangle,
  FaClipboardCheck, FaInfoCircle
} from 'react-icons/fa';
import PublicBackMethods from './PublicBackMethods';

const PublicEstablishmentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { public_request } = PublicBackMethods();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        // جلب بيانات المنشأة باستخدام الـ Request المحدث
        const response = await public_request.get(`/public/account/establishments/${id}/dashboard`);
        setData(response.data);
      } catch (error) {
        console.error("Error fetching details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDetails();
  }, [id]);

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
      <div className="spinner-grow text-primary" role="status">
        <span className="visually-hidden">جاري التحميل...</span>
      </div>
    </div>
  );

  if (!data) return (
    <div className="container mt-5">
      <div className="alert alert-danger shadow-sm rounded-4 d-flex align-items-center">
        <FaExclamationTriangle className="me-3 fs-3" />
        <div>لم يتم العثور على بيانات المنشأة المطلوبة.</div>
      </div>
      <button className="btn btn-outline-primary rounded-pill mt-3" onClick={() => navigate(-1)}>
        <FaArrowRight className="ms-2" /> العودة للخلف
      </button>
    </div>
  );

  const { establishment, rating } = data;

  return (
    <div className="container py-5" dir="rtl">
      {/* زر الرجوع */}
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <button
          className="btn btn-light shadow-sm rounded-pill px-4 d-flex align-items-center fw-bold text-secondary"
          onClick={() => navigate(-1)}
        >
          <FaArrowRight className="ms-2" /> رجوع
        </button>
        <div className="badge bg-white shadow-sm text-primary p-2 px-3 rounded-pill border">
          <FaInfoCircle className="ms-2" /> صفحة بيانات الفعالية
        </div>
      </div>

      {/* Header Card - التصميم المطور للهيدر */}
      <div className="card border-0 shadow rounded-4 overflow-hidden mb-4">
        <div className="p-4 text-white" style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #003d99 100%)' }}>
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="d-flex align-items-center mb-2">
                <div className="bg-white text-primary rounded-circle p-3 ms-3 shadow-sm">
                  <FaStore size={24} />
                </div>
                <div>
                  <h1 className="fw-bold mb-0 h3">{establishment.name}</h1>
                  <span className="opacity-75 small">
                    {establishment.facilityType} <span className="mx-1">•</span> {establishment.salesType}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-md-4 text-md-start mt-3 mt-md-0">
              <div className="bg-white bg-opacity-10 rounded-4 p-3 border border-white border-opacity-25 d-inline-block shadow-sm text-center">
                <div className="fw-bold h2 mb-0 text-warning">{rating.avg} <FaStar className="small" /></div>
                <div className="small opacity-75 text-white">{rating.count} تقييم للمنشأة</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body p-4 bg-light">
          <div className="row g-4">
            {/* معلومات الموقع */}
            <div className="col-md-6">
              <div className="p-3 bg-white rounded-4 shadow-sm h-100">
                <h5 className="fw-bold mb-3 text-primary d-flex align-items-center">
                  <FaMapMarkerAlt className="ms-2" /> الموقع الجغرافي
                </h5>
                <div className="mb-2"><strong>المحافظة:</strong> {establishment.governorate}</div>
                <div className="mb-2"><strong>المنطقة:</strong> {establishment.city} {establishment.district ? `- ${establishment.district}` : ''}</div>
                <div className="text-muted small lh-base">
                  <strong>العنوان:</strong> {establishment.addressText || "لم يتم تحديد عنوان تفصيلي"}
                </div>
              </div>
            </div>

            {/* معلومات السجل والاتصال */}
            <div className="col-md-6">
              <div className="p-3 bg-white rounded-4 shadow-sm h-100">
                <h5 className="fw-bold mb-3 text-primary d-flex align-items-center">
                  <FaFileAlt className="ms-2" /> السجل والاتصال
                </h5>
                <div className="mb-2"><strong>رقم السجل التجاري:</strong> <span className="badge bg-secondary">{establishment.tradeRegistryNumber}</span></div>
                <div className="mb-2"><strong>رقم الهاتف:</strong> {establishment.phone || "غير متوفر"}</div>
                <div className="small text-muted mt-2">
                  <strong>الموقع على الخريطة:</strong> {establishment.lat?.toFixed(5)}, {establishment.lng?.toFixed(5)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards - الإحصائيات */}
      <div className="row g-4 mb-4">
        <div className="col-md-6 col-lg-3">
          <div className="card border-0 shadow-sm rounded-4 text-center p-4 h-100 hover-up">
            <div className="bg-danger bg-opacity-10 text-danger rounded-pill p-2 mb-3 mx-auto" style={{ width: 'fit-content' }}>
              <FaExclamationTriangle size={24} />
            </div>
            <div className="h2 fw-bold text-danger mb-1">{establishment.violationReportsCount}</div>
            <div className="text-muted fw-bold">المخالفات المسجلة</div>
          </div>
        </div>
        <div className="col-md-6 col-lg-3">
          <div className="card border-0 shadow-sm rounded-4 text-center p-4 h-100 hover-up">
            <div className="bg-success bg-opacity-10 text-success rounded-pill p-2 mb-3 mx-auto" style={{ width: 'fit-content' }}>
              <FaClipboardCheck size={24} />
            </div>
            <div className="h2 fw-bold text-success mb-1">{establishment.inspectionVisitsCount}</div>
            <div className="text-muted fw-bold">الزيارات الرقابية</div>
          </div>
        </div>
        {/* هنا يمكن إضافة كروت إضافية مثل الشكاوى أو التقييمات */}
      </div>

      {/* حالة المنشأة - Alert */}
      {establishment.violationReportsCount === 0 ? (
        <div className="card border-0 shadow-sm rounded-4 p-4 bg-success bg-opacity-10 border-success border-start border-5">
          <div className="d-flex align-items-center">
            <FaShieldAlt className="text-success fs-2 ms-3" />
            <div>
              <h5 className="fw-bold text-success mb-1">المخالفات</h5>
              <p className="mb-0 text-dark opacity-75">هذه المنشأة ليس لديها مخالفات حالية مسجلة.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm rounded-4 p-4 bg-warning bg-opacity-10 border-warning border-start border-5">
          <div className="d-flex align-items-center">
            <FaExclamationTriangle className="text-warning fs-2 ms-3" />
            <div>
              <h5 className="fw-bold text-warning mb-1"></h5>
              <p className="mb-0 text-dark opacity-75">تم رصد مخالفات سابقة لهذه المنشأة، يرجى مراجعة التفاصيل عند الضرورة.</p>
            </div>
          </div>
        </div>
      )}

      <style>{`
                .hover-up { transition: all 0.3s ease; }
                .hover-up:hover { transform: translateY(-5px); }
                .card { transition: box-shadow 0.3s ease; }
            `}</style>
    </div>
  );
};

export default PublicEstablishmentDetailsPage;